import { useMemo, useState } from "react";
import { Button, Icon, Input, Modal } from "@legal-500/legal500-design-system";
import styles from "./MailingGroups.module.css";

type Props = {
  emails: string[];
  onRemove: (email: string) => void;
  onClearAll: () => void;
  onClose: () => void;
};

// Review surface for a hand-picked (explicit) selection. Because explicit picks
// now accumulate across filter changes, some selected emails aren't visible on
// the current page — this lists them all so the operator can search/remove them.
// Only the email is shown (name/firm aren't available client-side for emails not
// on the current page), which is acceptable for a review/removal surface.
export function SelectedRecipientsModal({ emails, onRemove, onClearAll, onClose }: Props) {
  const [search, setSearch] = useState("");

  const sorted = useMemo(() => [...emails].sort((a, b) => a.localeCompare(b)), [emails]);
  const term = search.trim().toLowerCase();
  const visible = term ? sorted.filter(email => email.toLowerCase().includes(term)) : sorted;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Selected recipients (${emails.length.toLocaleString()})`}
      size="medium"
      blurBackground
    >
      <div className={styles.modalForm}>
        <Input
          aria-label="Search selected recipients"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search email"
          fullWidth
        />

        <ul className={styles.targetsList}>
          {visible.length === 0 && (
            <li className={styles.mutedNote}>
              {emails.length === 0 ? "No recipients selected." : "No matching recipients."}
            </li>
          )}
          {visible.map(email => (
            <li key={email} className={styles.targetRow}>
              <span className={styles.targetEmail}>{email}</span>
              <Button
                variant="text"
                color="default"
                size="small"
                className={styles.removeButton}
                aria-label={`Remove ${email}`}
                onClick={() => onRemove(email)}
              >
                <Icon icon="cross" size="small" />
              </Button>
            </li>
          ))}
        </ul>

        <div className={styles.modalFooter}>
          <Button
            variant="outlined"
            color="default"
            onClick={onClearAll}
            disabled={emails.length === 0}
          >
            Clear all
          </Button>
          <Button variant="contained" color="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
